import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, ExternalLink, LogOut, Plus, Search } from "lucide-react";
import { ADMIN_NAV } from "./adminNav";
import { cx } from "./_shared";
import { usePermission } from "../../hooks/usePermission";

/** Bỏ dấu tiếng Việt để "dia diem" khớp "Địa điểm". */
const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

/**
 * Bảng lệnh (Ctrl/⌘ + K): nhảy nhanh tới trang admin hoặc chạy thao tác thường dùng.
 * ↑/↓ chọn, Enter mở, Esc đóng.
 */
export default function CommandPalette({ open, onClose, onLogout }) {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const commands = useMemo(() => {
    const pages = ADMIN_NAV.flatMap((g) =>
      g.items.filter((it) => !it.perm || can(it.perm)).map((it) => ({
        id: it.to,
        label: it.label,
        hint: g.title,
        icon: it.icon,
        run: () => navigate(it.to),
      }))
    );
    return [
      ...pages,
      ...(can("locations:write")
        ? [{ id: "act-new-location", label: "Thêm địa điểm mới", hint: "Thao tác", icon: Plus, run: () => navigate("/admin/locations?new=1") }]
        : []),
      { id: "act-site", label: "Xem website", hint: "Thao tác", icon: ExternalLink, run: () => window.open("/", "_blank", "noopener") },
      { id: "act-logout", label: "Đăng xuất", hint: "Thao tác", icon: LogOut, run: onLogout },
    ];
  }, [navigate, onLogout, can]);

  const results = useMemo(() => {
    const q = norm(query).trim();
    if (!q) return commands;
    return commands.filter((c) => norm(`${c.label} ${c.hint}`).includes(q));
  }, [commands, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const choose = (cmd) => {
    if (!cmd) return;
    onClose();
    cmd.run();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[160] flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Tìm nhanh">
      <button
        type="button"
        aria-label="Đóng"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        style={{ animation: "admin-fade-in 0.15s ease-out" }}
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-soft-xl"
        style={{ animation: "admin-pop-in 0.16s ease-out" }}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-3 border-b border-admin-border px-4">
          <Search size={17} className="shrink-0 text-admin-textMuted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm trang hoặc thao tác…"
            aria-label="Tìm trang hoặc thao tác"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmd-list"
            className="h-14 min-w-0 flex-1 bg-transparent text-[15px] text-admin-text outline-none placeholder:text-admin-textMuted/70"
          />
          <kbd className="hidden rounded-md border border-admin-border bg-admin-muted px-1.5 py-0.5 text-[10px] font-semibold text-admin-textMuted sm:block">Esc</kbd>
        </div>

        <ul id="cmd-list" ref={listRef} role="listbox" className="max-h-[min(50vh,380px)] overflow-y-auto p-2">
          {results.length === 0 ? (
            <li className="px-3 py-10 text-center text-sm text-admin-textMuted">Không tìm thấy “{query}”.</li>
          ) : (
            results.map((c, i) => {
              const Icon = c.icon;
              const on = i === active;
              return (
                <li key={c.id} role="option" aria-selected={on} data-idx={i}>
                  <button
                    type="button"
                    onMouseMove={() => setActive(i)}
                    onClick={() => choose(c)}
                    className={cx(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                      on ? "bg-admin-primaryLight text-admin-primary" : "text-admin-text"
                    )}
                  >
                    <Icon size={16} className={on ? "text-admin-primary" : "text-admin-textMuted"} />
                    <span className="flex-1 truncate font-medium">{c.label}</span>
                    <span className="text-xs text-admin-textMuted">{c.hint}</span>
                    {on ? <CornerDownLeft size={13} className="text-admin-primary" /> : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <div className="flex items-center gap-4 border-t border-admin-border bg-admin-muted/40 px-4 py-2 text-[11px] text-admin-textMuted">
          <span>↑↓ chọn</span>
          <span>↵ mở</span>
          <span>Esc đóng</span>
        </div>
      </div>
    </div>
  );
}
